import * as React from 'react'
import { ChangeEvent, useCallback, useEffect, useMemo, useState } from 'react'
import MuiTextField, {
  TextFieldProps as MuiTextFieldProps,
} from '@mui/material/TextField'
import { FieldProps, getIn } from 'formik'
import { updatePhoneValueForm } from '@sales-center/common'
import { debounce } from 'lodash'

type TextFieldProps = Omit<MuiTextFieldProps, 'name' | 'value' | 'error'>

type Props = Omit<TextFieldProps, 'name' | 'value' | 'error'> &
  FieldProps & { debounceDelay?: number; avoidValidateOnBlur?: boolean }

export const MuiPhoneFieldFormik: React.FC<Props> = ({
  debounceDelay = 300,
  form: { isSubmitting, errors, touched, setFieldValue },
  disabled = isSubmitting,
  field: { onBlur: fieldOnBlur, ...field },
  onBlur: propsOnBlur,
  onChange: propsOnChange,
  size = 'small',
  fullWidth = true,
  avoidValidateOnBlur = false,
  margin = 'dense',
  ...props
}) => {
  const [value, setValue] = useState<string>(field.value ?? '')

  const update = useMemo(
    () => debounce((v) => setFieldValue(field.name, v), debounceDelay),
    [debounceDelay, field.name, setFieldValue],
  )

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key.match(/Backspace/)) {
        if (value.length >= 2 && value[value.length - 2] === '-')
          setValue(value.slice(0, value.length - 1))
      }
    },
    [value],
  )

  const errorText = getIn(errors, field.name)
  const error = getIn(touched, field.name) && errorText !== undefined

  const onBlur = React.useCallback(
    (event: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (propsOnBlur) propsOnBlur(event)
      fieldOnBlur(event ?? field.name)
    },
    [field.name, fieldOnBlur, propsOnBlur],
  )

  const onChange = useCallback(
    (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      if (propsOnChange) propsOnChange(event)

      const result = event.target.value.replace(/\D/g, '')

      if (result.length <= 10)
        setValue(
          updatePhoneValueForm(
            result,
            event.target.value.length < value.length,
          ),
        )
    },
    [propsOnChange, value.length],
  )

  useEffect(() => {
    update(value)
    return () => update.cancel()
  }, [value, update])

  useEffect(() => {
    setValue(field.value ?? '')
    update.cancel()
  }, [field.value, update])

  const textFieldProps = {
    ...field,
    ...props,
    disabled,
    error,
    helperText: !!errorText && error ? errorText : props.helperText,
    onBlur: propsOnBlur
      ? propsOnBlur
      : !avoidValidateOnBlur
        ? onBlur
        : undefined,
  }
  return (
    <MuiTextField
      {...textFieldProps}
      onChange={onChange}
      onKeyDown={handleKeyDown}
      fullWidth={fullWidth}
      margin={margin}
      size={size}
      value={value}
    />
  )
}
MuiPhoneFieldFormik.displayName = 'MuiPhoneFieldFormik'
